import { ChangeDetectionStrategy, Component, Inject, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';

import { OptionBundle, PackagingFormat, PackagingOptions } from '@mtna/rds-packaging-ui';

import { RdsBundleSelectionEvent } from '../package-bundle-selection/index';

export class RdsFormatSelectionDialogData {
  /**
   * Data for the format selection dialog
   * @param bundles preconfigured packaging bundles
   * @param options all the packaging options
   * @param icon icon for the header, defaults to 'info'
   * @param title title for the header, defaults to 'Package Formats and Bundles'
   * @param description
   * description for the header, defaults to 'Select any export options and/or preconfigured bundles you may be interested in.'
   * @param color theme color for dialog controls, defaults to 'primary'
   * @param contentHeight height of the scrollable dialog content, defaults to '700px'
   */
  constructor(
    public bundles: Array<OptionBundle>,
    public options: PackagingOptions,
    public icon: string = 'info',
    public title: string = 'Package Formats and Bundles',
    public description: string = `Select any export options and/or preconfigured bundles you may be interested in.`,
    public color: 'primary' | 'accent' = 'primary',
    public contentHeight: string = '700px'
  ) {}
}

export class RdsBundlesAndFormats {
  /**
   * Output on dialog close when the affirmative button is selected/clicked
   * @param bundles selected packaging bundles
   * @param formats selected packaging formats
   */
  constructor(public bundles: Array<OptionBundle>, public formats: Array<PackagingFormat>) {}
}

/**
 * A dialog for selecting packaging bundles and formats
 *
 * @author Will Davis <will.davis@mtna.us>
 */
@Component({
  selector: 'rds-format-selection-dialog',
  templateUrl: './format-selection-dialog.component.html',
  styleUrls: ['./format-selection-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class RdsFormatSelectionDialogComponent {
  _selectedBundles: Array<OptionBundle> = [];
  _selectedFormats: Array<PackagingFormat> = [];

  /** Getter for the output data on affirmative close */
  get selectedBundlesAndFormats(): RdsBundlesAndFormats {
    return new RdsBundlesAndFormats(this._selectedBundles, this._selectedFormats);
  }

  constructor(@Inject(MAT_DIALOG_DATA) public data: RdsFormatSelectionDialogData) {}

  handleBundleSelectionChange(event: RdsBundleSelectionEvent) {
    this._selectedBundles = event.selected
      ? [...this._selectedBundles, event.bundle]
      : this._selectedBundles.filter(b => b.id !== event.bundle.id);
  }

  handleFormatSelectionAdd(event: PackagingFormat) {
    this._selectedFormats = [...this._selectedFormats, event];
  }

  handleFormatSelectionRemove(event: PackagingFormat) {
    const index = this._selectedFormats.findIndex(f => f.id === event.id);
    const copy = [...this._selectedFormats];
    copy.splice(index, 1);
    this._selectedFormats = copy;
  }
}
